<script lang="ts" setup>
import MCheckboxGroup from "../MCheckboxGroup";
import MFormRepeatGroup from "../MFormRepeatGroup";
import { MCheckbox, MTelephoneNumberInput } from "../inputs";
import MBanner from "../MBanner";
import MButton from "../MButton";
import MRadioGroup from "../MRadioGroup";
import MSimpleSelect from "../inputs/MSimpleSelect";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

const props = defineProps({
  recipientOptions: Array,
  availableCommunicationChannelTypes: Array,
  hideOptions: {
    type: Boolean,
    default: false,
  },
  checkBoxRequired: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits({
  recipientsUpdated: (_value: any) => true,
});

const displayEditRecipientDetails = ref(false);
const recipients = ref([]);
const customisedRecipients = ref([]);

const formattedRecipientOptions = computed(() => {
  return props.recipientOptions.map((option) => {
    return {
      ...option,
      disable: !option.recipientHasViableChannel && !option.defaultChannelValue,
    };
  });
});

onMounted(() => {
  if (!getCurrentInstance()?.vnode.props[`onRecipientsUpdated`]) {
    console.error("recipientsUpdated emit was not provided");
  }

  if (!props.hideOptions) {
    recipients.value = formattedRecipientOptions.value
      .filter((recipient) => !recipient.disable)
      .map((recipient) => recipient.value);
    emit("recipientsUpdated", recipients);
  }
});

watch(
  () => customisedRecipients,
  () => {
    recipients.value = [];

    customisedRecipients.value.forEach((recipient, index) => {
      if (recipient.includeRecipient) {
        const adHocTelephoneNumber = recipient.useDefaultTelephoneNumber
          ? recipient.useDefaultTelephoneNumber
          : recipient.adhocTelephoneNumberForSMS;

        recipients.value.push({
          recipientId: props.recipientOptions[index].value.recipientId,
          recipientType: props.recipientOptions[index].value.recipientType,
          defaultChannelOverride:
            recipient.preferredChannelType !== "default" ? recipient.preferredChannelType : null,
          adhocTelephoneNumberForSMS:
            recipient.preferredChannelType === "sms-message" ? adHocTelephoneNumber : undefined,
          adhocEmailAddress:
            recipient.preferredChannelType === "email"
              ? props.recipientOptions[index].defaultCorrespondenceEmailAddress
              : undefined,
          newCommunicationId: recipient.newCommunicationId,
        });
      }
    });
    emit("recipientsUpdated", recipients);
  },
  { deep: true },
);

function getSelectedChannelType(recipient) {
  return props.availableCommunicationChannelTypes.find(
    (channelType) => channelType.value === recipient.preferredChannelType,
  );
}

function showEditRecipientDetails() {
  props.recipientOptions.forEach((recipientOption) => {
    const recipientIsIncludedInTheCurrentRecipientsSelection = !!recipients.value.find(
      (recipient) => recipient.recipientId === recipientOption.value.recipientId,
    );

    let telephoneNumberOptions = [];
    recipientOption.telephoneNumbers.map((number) => {
      telephoneNumberOptions.push({
        label: number.label,
        value: number.value,
      });
    });
    telephoneNumberOptions.push({
      label: "Use one-off phone number",
      value: false,
    });

    let communicationChannelOptions = [];
    recipientOption.filteredAvailableCommunicationChannelOptions.forEach((option) => {
      communicationChannelOptions.push({
        value: option.value,
        label: option.formattedLabel,
      });
    });
    if (recipientOption.defaultChannelValue) {
      communicationChannelOptions.unshift({
        value: "default",
        label: `Default channel (${recipientOption.defaultChannelValue})`,
      });
    }

    customisedRecipients.value.push({
      ...recipientOption,
      ...recipientOption.value,
      name: recipientOption.name,
      includeRecipient: recipientIsIncludedInTheCurrentRecipientsSelection,
      preferredChannelType: recipientOption.preferredChannelType,
      useDefaultTelephoneNumber: telephoneNumberOptions[0].value,
      adhocTelephoneNumberForSMS: null,
      telephoneNumberDetails: null,
      recipientHasValidTelephoneNumber: !!recipientOption.defaultTelephoneNumberForSMS,
      telephoneNumberOptions,
      communicationChannelOptions,
    });
  });

  displayEditRecipientDetails.value = true;
}
</script>

<template>
  <m-checkbox-group
    v-if="!displayEditRecipientDetails"
    v-model="recipients"
    :required="checkBoxRequired"
    label="Send to"
    :items="formattedRecipientOptions"
  />

  <template v-if="displayEditRecipientDetails">
    <p>Send to <span class="error-text">*</span></p>

    <m-form-repeat-group
      v-for="(recipient, recipientIndex) in customisedRecipients"
      :key="recipientIndex"
      required
    >
      <m-checkbox v-model="recipient.includeRecipient" :label="recipient.name" :value="true" />

      <template v-if="recipient.includeRecipient">
        <m-simple-select
          v-model="recipient.preferredChannelType"
          label="Send via"
          :options="recipient.communicationChannelOptions"
          required
        />

        <m-radio-group
          v-if="getSelectedChannelType(recipient)?.isSMSMessage"
          v-model="recipient.useDefaultTelephoneNumber"
          label="Number"
          :items="recipient.telephoneNumberOptions"
        />

        <template
          v-if="
            getSelectedChannelType(recipient)?.isSMSMessage && !recipient.useDefaultTelephoneNumber
          "
        >
          <m-telephone-number-input
            v-model="recipient.adhocTelephoneNumberForSMS"
            label="One-off number"
            required
            @update="recipient.telephoneNumberDetails = $event"
          />

          <m-banner v-if="recipient.telephoneNumberDetails?.isValid === false" type="warning">
            The number you have entered is invalid for sending text messages and will not be used.
          </m-banner>
        </template>
      </template>
    </m-form-repeat-group>
  </template>

  <div v-if="!displayEditRecipientDetails" class="flex justify-start">
    <m-button label="Modify recipient details" ghost @click="showEditRecipientDetails" />
  </div>
</template>

<style lang="scss"></style>
